import React from 'react';
import { Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';

type BaseProps = {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  children?: React.ReactNode;
};

type ButtonAsButtonProps = BaseProps & React.ButtonHTMLAttributes<HTMLButtonElement> & {
  href?: never;
};

type ButtonAsLinkProps = BaseProps & React.AnchorHTMLAttributes<HTMLAnchorElement> & {
  href: string;
};

export type ButtonProps = ButtonAsButtonProps | ButtonAsLinkProps;

export const Button = React.forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  (
    {
      children,
      className = '',
      variant = 'primary',
      size = 'md',
      loading = false,
      leftIcon,
      rightIcon,
      ...props
    },
    ref
  ) => {
    // Core premium transitions, active scale elastic behavior, and focus rings
    const baseStyles =
      'inline-flex items-center justify-center font-heading font-extrabold transition-all duration-300 rounded-xl active:scale-[0.97] disabled:scale-100 disabled:opacity-50 disabled:cursor-not-allowed select-none focus:outline-none';

    // Customized variants mapped directly to our elegant brand color system
    const variantStyles = {
      primary:
        'bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-500/10 border border-blue-500/10 hover:translate-y-[-1px]',
      secondary:
        'bg-indigo-500 hover:bg-indigo-600 text-slate-950 hover:text-white shadow-lg shadow-indigo-500/10 border border-indigo-500/10 hover:translate-y-[-1px]',
      outline: 'border border-slate-200 bg-white hover:bg-slate-50 text-slate-700',
      ghost: 'text-slate-700 hover:bg-slate-50 hover:text-slate-950',
    };

    // Harmonized visual weights and scaling sizes
    const sizeStyles = {
      sm: 'px-3 py-1.5 text-[11px] gap-1.5',
      md: 'px-5 py-2.5 text-xs gap-2',
      lg: 'px-8 py-3.5 text-sm gap-2.5',
    };

    const content = (
      <>
        {loading && <Loader2 className="w-3.5 h-3.5 animate-spin flex-shrink-0" />}
        {!loading && leftIcon && <span className="flex-shrink-0">{leftIcon}</span>}
        {children}
        {!loading && rightIcon && <span className="flex-shrink-0">{rightIcon}</span>}
      </>
    );

    const classes = `${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${className}`;

    // If it's a link (has href)
    if ('href' in props && props.href) {
      const { href, ...linkProps } = props as ButtonAsLinkProps;
      return (
        <Link
          to={href}
          className={classes}
          ref={ref as React.Ref<HTMLAnchorElement>}
          {...linkProps}
        >
          {content}
        </Link>
      );
    }

    // Default: regular button
    const { type = 'button', disabled, ...buttonProps } = props as ButtonAsButtonProps;
    const isDisabled = disabled || loading;

    return (
      <button
        type={type}
        className={classes}
        disabled={isDisabled}
        ref={ref as React.Ref<HTMLButtonElement>}
        {...buttonProps}
      >
        {content}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;

